import { useQuery } from "react-query";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { MediaAnalyticsApiParams } from "@circle-react/helpers/urlHelpers/internalApi/analytics";

interface AnalyticsTimelineParams {
  dateGrouping?: string;
  endDate?: string;
  signedId: string;
  startDate?: string;
}

// Timeline response type definitions
interface ViewersTimelineData {
  date_period: string;
  unique_viewers: number;
  views: number;
}

interface AverageProgressTimelineData {
  avg_progress_percent: number;
  date_period: string;
}

type TimelineType = "average_progress_timeline" | "viewers_timeline";

interface TimelineResponseMap {
  average_progress_timeline: AverageProgressTimelineData[];
  viewers_timeline: ViewersTimelineData[];
}

/**
 * Generic hook for fetching video analytics timeline data
 */
export const useVideoAnalyticsTimeline = <T extends TimelineType>(
  timelineType: T,
  { signedId, startDate, endDate, dateGrouping }: AnalyticsTimelineParams,
) => {
  const params: MediaAnalyticsApiParams = {
    filters: {
      signed_id_filter: {
        key: "signed_id",
        value: signedId,
      },
    },
  };

  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (dateGrouping) params.date_grouping = dateGrouping;

  const fetchTimeline = (): Promise<TimelineResponseMap[T]> =>
    reactQueryGet(internalApi.analytics.media.timeline[timelineType](params));

  return useQuery(
    [
      "video-analytics-timeline",
      timelineType,
      signedId,
      startDate,
      endDate,
      dateGrouping,
    ],
    fetchTimeline,
    {
      enabled: Boolean(signedId),
    },
  );
};

/**
 * Specific timeline hooks - convenient wrappers around the generic hook
 */
export const useVideoAnalyticsViewersTimeline = (
  params: AnalyticsTimelineParams,
) => useVideoAnalyticsTimeline("viewers_timeline", params);

export const useVideoAnalyticsAverageProgressTimeline = (
  params: AnalyticsTimelineParams,
) => useVideoAnalyticsTimeline("average_progress_timeline", params);
